import { Image as AntdImage, Space, type ImageProps as AntdImageProps } from 'antd';
import React, { useState } from 'react';

import { Button, ButtonVariant, Icon } from '@/shared/ui';

import { ImageBase64 } from '@/helpers/fileHelper';

import type { ImgPath, ExternalImgPath } from './Image';

export interface PreviewImageProps extends Omit<AntdImageProps, 'src'> {
  src: ImgPath | ExternalImgPath | ImageBase64 | '#';
  fallback?: ImgPath;
}

export const defaultPreview = {
  movable: true,
  actionsRender: (
    _: React.ReactElement,
    {
      transform: { scale },
      actions: { onZoomOut, onZoomIn },
    }: {
      transform: { scale: number };
      actions: { onZoomOut: () => void; onZoomIn: () => void };
    },
  ): React.ReactNode => (
    <Space size={12} className="toolbar-wrapper">
      <Button variant={ButtonVariant.ICON} disabled={scale === 1} onClick={onZoomOut}>
        <Icon name="zoom_out" />
      </Button>
      <Button variant={ButtonVariant.ICON} disabled={scale === 50} onClick={onZoomIn}>
        <Icon name="zoom_in" />
      </Button>
    </Space>
  ),
};

export const PreviewImage: React.FC<PreviewImageProps> = ({
  src,
  fallback,
  preview = true,
  onError,
  className,
  ...rest
}) => {
  const [hasError, setHasError] = useState<boolean>(src === '#');

  const handleError = (event: React.SyntheticEvent<HTMLImageElement, Event>): void => {
    if (!hasError && fallback) {
      setHasError(true);
    }
    onError?.(event);
  };

  const imageSrc = hasError && fallback ? fallback : src;

  if (imageSrc === '#') {
    return null;
  }

  const previewConfig =
    preview === true
      ? {
          ...defaultPreview,
        }
      : typeof preview === 'object' && preview !== null
        ? {
            ...preview,
          }
        : preview;

  return (
    <div className={className}>
      <AntdImage src={imageSrc} preview={previewConfig} onError={handleError} {...rest} />
    </div>
  );
};
